/**
 * History Transform Functions for Caster Mold
 * Transforms API history responses to frontend models
 */

import type { PartDetailsHistoryAPI } from "../api/types";
import type { HistoryEntry } from "../../../../shared/models";
import { isStrandContainer } from "../constants/parts";

type HistoryAction =
  | "Installed"
  | "Uninstalled"
  | "Repaired"
  | "Created"
  | "Online"
  | "Offline";

// ============================================
// Helper Functions
// ============================================

/**
 * Determine the action type from API row flags
 *
 * Business logic:
 * - IsReset=true -> "Repaired"
 * - IsDefaultArray=true & PrevParentPartId=null -> "Created" (first task in spares)
 * - IsDefaultArray=true & PrevParentPartId exists -> "Uninstalled" (removed from position)
 * - Otherwise -> "Installed" (placed on position)
 */
const determineAction = (row: PartDetailsHistoryAPI): HistoryAction => {
  if (row.IsReset) {
    return "Repaired";
  }

  if (row.IsDefaultArray) {
    return row.PrevParentPartId === null ? "Created" : "Uninstalled";
  }

  return "Installed";
};

/**
 * Build a location display string from parent and position names
 *
 * Format: "ParentName - PositionName" or just the available name
 */
const buildLocationDisplay = (
  parentName: string | null,
  positionName: string | null,
): string | null => {
  if (parentName && positionName) {
    return `${parentName} - ${positionName}`;
  }
  return parentName || positionName || null;
};

/**
 * Get the appropriate location name based on action type
 *
 * - For Uninstall: shows where it was uninstalled FROM (previous location)
 * - For Install/Repair: shows where it was installed TO (current location)
 */
const getLocationName = (
  row: PartDetailsHistoryAPI,
  action: HistoryAction,
): string | null => {
  if (action === "Uninstalled" && row.PrevParentPartName) {
    return buildLocationDisplay(row.PrevParentPartName, row.PrevPositionName);
  }

  return buildLocationDisplay(row.ParentPartName, row.PositionName);
};

/**
 * Combine part info fields into a single display string
 *
 * Format: "SerialNumber | Make | Model | Vendor" (filters out nulls/empty)
 * Falls back to PartInfo if individual fields not available
 */
const combinePartInfo = (row: PartDetailsHistoryAPI): string | null => {
  const parts = [
    row.SerialNumber,
    row.WarehouseNumber,
    row.DrawingNumber,
  ].filter((v) => v !== null && v !== undefined && v !== "");
  return parts.length > 0 ? parts.join(" | ") : null;
};

// ============================================
// Filter Functions
// ============================================

/**
 * Group position IDs to filter out from history.
 * These are install/uninstall operations of group assemblies (Flanges=7, Collars=8, Seals=9)
 * and Foot Roll Assemblies (10-13) — not user-replaceable parts.
 */
const GROUP_POSITION_IDS = new Set([7, 8, 9, 10, 11, 12, 13]);

/**
 * Check if a history row is a group fixture install/uninstall operation
 * that should be filtered out.
 * Checks PositionId (install target) and PrevPositionId (uninstall source).
 */
const isGroupFixtureOperation = (row: PartDetailsHistoryAPI): boolean =>
  (row.PositionId !== null && GROUP_POSITION_IDS.has(row.PositionId)) ||
  (row.PrevPositionId !== null && GROUP_POSITION_IDS.has(row.PrevPositionId));

/**
 * Check if a history row is a mold install/uninstall operation
 * that should be filtered out.
 * Molds are installed on strand containers — checks ParentPartId against known strand IDs.
 */
const isMoldOperation = (row: PartDetailsHistoryAPI): boolean =>
  isStrandContainer(row.ParentPartId ?? undefined) ||
  isStrandContainer(row.PrevParentPartId ?? undefined);

// ============================================
// Main Transform Function
// ============================================

/**
 * Transform API response from GetCasterPartDetails history to frontend HistoryEntry[]
 *
 * Filters out:
 * - Mold install/uninstall operations (structural)
 * - Group fixture operations (Flanges, Collars, Seals, Foot Roll Assembly)
 *
 * @param apiData - Raw history rows from EQP.GetCasterPartDetails
 * @returns Formatted history entries for UI display
 */
export const transformHistoryAPIToEntries = (
  apiData: PartDetailsHistoryAPI[],
): HistoryEntry[] => {
  const filteredData = apiData.filter(
    (row) => !isMoldOperation(row) && !isGroupFixtureOperation(row),
  );

  return filteredData.map((row) => {
    const action = determineAction(row);
    const locationName = getLocationName(row, action);
    const partInfo = combinePartInfo(row);

    return {
      HistoryId: row.TaskId,
      Date: row.Timestamp
        ? row.Timestamp instanceof Date
          ? row.Timestamp
          : new Date(row.Timestamp)
        : null,
      Action: action,
      LocationName: locationName,
      Heats: row.Heats ?? 0,
      User: row.User,
      Notes: row.Notes,
      PartInfo: partInfo,
      PartId: row.PartId ?? null,
      IsReset: row.IsReset ?? false,
      IsDefaultArray: row.IsDefaultArray ?? false,
      PreviousTask: row.PreviousTask ?? null,
    };
  });
};
